import { Switch } from "@/clientWeb/components/ui/switch";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";

/**
 * Presentation-only component. Every toggle is prop-driven so the feature
 * layer can persist values and wire side-effects without `clientVc/` having
 * to reach into a store directly.
 */
interface AdvancedSettingsProps {
  permanentBottomBar?: boolean
  onPermanentBottomBarChange?: (value: boolean) => void
  hiddenSelfVideo?: boolean
  onHiddenSelfVideoChange?: (value: boolean) => void
  sideDrawerOverContent?: boolean
  onSideDrawerOverContentChange?: (value: boolean) => void
  notifications?: boolean
  onNotificationsChange?: (value: boolean) => void
  notificationSounds?: boolean
  onNotificationSoundsChange?: (value: boolean) => void
  autoPiP?: boolean
  onAutoPiPChange?: (value: boolean) => void
  autoHideNames?: boolean
  onAutoHideNamesChange?: (value: boolean) => void
  hideChatNotifications?: boolean
  onHideChatNotificationsChange?: (value: boolean) => void
  hideTilesWithoutVideo?: boolean
  onHideTilesWithoutVideoChange?: (value: boolean) => void
}

export function AdvancedSettings({
  permanentBottomBar = false,
  onPermanentBottomBarChange,
  hiddenSelfVideo = false,
  onHiddenSelfVideoChange,
  sideDrawerOverContent = false,
  onSideDrawerOverContentChange,
  notifications = true,
  onNotificationsChange,
  notificationSounds = true,
  onNotificationSoundsChange,
  autoPiP = false,
  onAutoPiPChange,
  autoHideNames = false,
  onAutoHideNamesChange,
  hideChatNotifications = false,
  onHideChatNotificationsChange,
  hideTilesWithoutVideo = false,
  onHideTilesWithoutVideoChange,
}: AdvancedSettingsProps) {
  const t = useTranslation();

  return (
    <div className="flex-1 max-h-[400px] overflow-x-hidden overflow-y-auto px-2 space-y-4">
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.permanentBottomBar")}
        </span>
        <Switch checked={permanentBottomBar} onCheckedChange={onPermanentBottomBarChange} />
      </div>

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.hiddenSelfVideo")}
        </span>
        <Switch checked={hiddenSelfVideo} onCheckedChange={onHiddenSelfVideoChange} />
      </div>

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.sideDrawerOverContent")}
        </span>
        <Switch
          checked={sideDrawerOverContent}
          onCheckedChange={onSideDrawerOverContentChange}
        />
      </div>

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.notifications")}
        </span>
        <Switch checked={notifications} onCheckedChange={onNotificationsChange} />
      </div>

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.notificationSounds")}
        </span>
        <Switch
          checked={notificationSounds}
          onCheckedChange={onNotificationSoundsChange}
        />
      </div>

      {/* Only when the feature layer offers it — it withholds the handler on
          phone browsers, which have no Picture-in-Picture to open. */}
      {onAutoPiPChange && (
        <div className="flex items-center justify-between gap-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm text-slate-700 dark:text-foreground">
              {t("controlBar.autoPiP")}
            </span>
            {/* Auto-PiP also needs the browser's own per-site "Automatic
                picture-in-picture" permission, which a page can neither read
                nor change — blocked on one site, it silently never opens there
                while working on another. Only Chrome/Edge (Document PiP) have
                it, so elsewhere the hint would point at a setting that isn't. */}
            {autoPiP && typeof window !== "undefined" && "documentPictureInPicture" in window && (
              <span className="text-xs text-slate-500 dark:text-muted-foreground">
                {t("controlBar.autoPiPDesc")}
              </span>
            )}
          </div>
          <Switch checked={autoPiP} onCheckedChange={onAutoPiPChange} />
        </div>
      )}

      <div className="flex items-center justify-between py-2">
        <div className="flex flex-col">
          <span className="text-sm text-slate-700 dark:text-foreground">
            {t("controlBar.autoHideNames")}
          </span>
          <span className="text-xs text-slate-500 dark:text-muted-foreground">
            {t("controlBar.autoHideNamesDesc")}
          </span>
        </div>
        <Switch checked={autoHideNames} onCheckedChange={onAutoHideNamesChange} />
      </div>

      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.hideChatNotifications")}
        </span>
        <Switch
          checked={hideChatNotifications}
          onCheckedChange={onHideChatNotificationsChange}
        />
      </div>

      {/* The grid has honoured this since it was written; there was simply no
          way to turn it on. Backwards from the usual problem in this dialog. */}
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">
          {t("controlBar.hideNoVideoParticipants")}
        </span>
        <Switch
          checked={hideTilesWithoutVideo}
          onCheckedChange={onHideTilesWithoutVideoChange}
        />
      </div>
    </div>
  );
}
